import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLandingContent } from "@/features/admin/content/landingContent";

const isExternal = (h: string) => /^https?:\/\//.test(h);

/**
 * Carrusel a todo lo ancho de la pantalla. Cada diapositiva es una imagen
 * editable desde el admin, con un enlace de redirección opcional. Si no hay
 * diapositivas, la sección no se muestra.
 */
export function Carousel() {
  const { carousel } = useLandingContent();
  const [index, setIndex] = useState(0);
  const count = carousel.length;

  const go = useCallback((i: number) => setIndex(((i % count) + count) % count), [count]);

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 5000);
    return () => clearInterval(t);
  }, [count]);

  if (count === 0) return null;

  const slideInner = (slide: { image: string; link: string }) => (
    <>
      {/* Fondo borroso del mismo color para rellenar el espacio sobrante sin
          recortar la imagen (mismo patrón que el lobby de identity-web). */}
      <img
        aria-hidden="true"
        src={slide.image}
        className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-2xl brightness-[0.55]"
      />
      {/* Imagen completa, sin recorte. */}
      <img
        src={slide.image}
        alt=""
        className="relative h-full w-full object-contain object-center"
      />
    </>
  );

  return (
    <section className="relative w-full overflow-hidden bg-neutral-950">
      <div
        className="flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {carousel.map((slide) => (
          <div key={slide.id} className="relative aspect-[16/9] sm:aspect-[2/1] lg:aspect-[12/5] max-h-[540px] w-full shrink-0 overflow-hidden">
            {slide.link ? (
              isExternal(slide.link) ? (
                <a href={slide.link} target="_blank" rel="noreferrer" className="block h-full w-full">
                  {slideInner(slide)}
                </a>
              ) : (
                <a href={slide.link} className="block h-full w-full">
                  {slideInner(slide)}
                </a>
              )
            ) : (
              slideInner(slide)
            )}
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            onClick={() => go(index - 1)}
            aria-label="Anterior"
            className="absolute left-4 top-1/2 -translate-y-1/2 grid size-10 place-items-center rounded-full bg-black/40 text-white backdrop-blur hover:bg-black/60"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            onClick={() => go(index + 1)}
            aria-label="Siguiente"
            className="absolute right-4 top-1/2 -translate-y-1/2 grid size-10 place-items-center rounded-full bg-black/40 text-white backdrop-blur hover:bg-black/60"
          >
            <ChevronRight className="size-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {carousel.map((s, i) => (
              <button
                key={s.id}
                onClick={() => go(i)}
                aria-label={`Ir a la diapositiva ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === index ? "w-6 bg-white" : "w-2 bg-white/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
